import React, { useState } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames/bind'
import { Tab as SemanticTab } from 'semantic-ui-react'
import MobileNav from '../mobileNav'
import styles from './styles.scss'
const cx = classnames.bind(styles)

export const getMenuItemData = (menuItem, index) => {
  if (!menuItem)
    return {
      key: '',
      text: '',
      value: '',
    }

  if (typeof menuItem === 'object' && typeof menuItem.content === 'string') {
    return {
      key: menuItem.key,
      text: menuItem.content,
      value: menuItem.content,
    }
  }
  if (typeof menuItem === 'object' && React.isValidElement(menuItem.content)) {
    return {
      key: menuItem.key,
      text: menuItem.content,
      // TODO: Ask Steve. Technically it's wrong to fall back on menuItem.content for value
      // since it's a JSX element, but it's added for backwards compat at the moment
      value: menuItem.value || menuItem.content,
    }
  }
  return {
    key: index,
    text: menuItem,
    value: menuItem,
  }
}

const getDropdownOptions = panes => {
  return panes.map(({ menuItem }, index) => getMenuItemData(menuItem, index))
}

const CustomCollapseWrapper = ({ renderer, index, label }) => (
  <div
    data-elm-id='tabs_custom_collapse_wrapper'
    className={cx('custom-collapse-wrapper')}
  >
    {renderer({ index, label, defaultClassName: cx('custom-collapse') })}
  </div>
)

CustomCollapseWrapper.propTypes = {
  renderer: PropTypes.func,
  index: PropTypes.number,
  label: PropTypes.string,
}

const Tab = ({
  elmId = 'default',
  children,
  className,
  role = 'primary',
  collapsing = true,
  menu = { secondary: true, pointing: true },
  panes,
  onTabChange,
  activeIndex,
  customCollapseRenderer,
  transparentMode,
}) => {
  const dropDownOptions = getDropdownOptions(panes)
  const [activeTabIndex, setActiveTabIndex] = useState(activeIndex || 0)
  const [activeTabValue, setActiveTabValue] = useState(
    dropDownOptions[activeTabIndex]?.value
  )

  const defaultOnTabChange = (e, data) => {
    setActiveTabIndex(data.activeIndex)
    setActiveTabValue(dropDownOptions[data.activeIndex].value)
    if (onTabChange) onTabChange(e, data)
  }

  const finalActiveTabIndex = activeIndex || activeTabIndex

  const defaultOnDropdownChange = (e, { value }) => {
    const tabs = panes
    const activeIndex = tabs.findIndex(
      tab => value === getMenuItemData(tab.menuItem, null).value
    )
    /**
     * This will emulate the semantic-ui's onTabChange event and pass the same data (essentially, but
     * just including the panes and activeIndex for now...)
     * This is to keep the onTabChange event consistent between the tabs and dropdown
     */
    defaultOnTabChange(null, {
      panes,
      activeIndex,
    })
  }

  const hasPanes = panes?.[0]?.render

  return (
    <div
      data-elm-id={`tabs_container_${elmId}`}
      className={cx(
        'tab-container',
        role,
        { collapsing, 'has-panes': hasPanes },
        className
      )}
    >
      {customCollapseRenderer && (
        <CustomCollapseWrapper
          renderer={customCollapseRenderer}
          index={finalActiveTabIndex}
          label={
            getMenuItemData(
              panes?.[finalActiveTabIndex]?.menuItem,
              finalActiveTabIndex
            ).text
          }
        />
      )}

      {!customCollapseRenderer && (
        <MobileNav
          data-elm-id={`tabs_dropdown_${elmId}`}
          options={dropDownOptions}
          value={activeTabValue}
          onChange={defaultOnDropdownChange}
        />
      )}
      <SemanticTab
        data-elm-id={`tabs_${elmId}`}
        className={cx(
          'tabs-and-panes',
          { collapsing, 'has-panes': hasPanes },
          { 'transparent-mode': transparentMode },
          className
        )}
        activeIndex={finalActiveTabIndex}
        onTabChange={defaultOnTabChange}
        panes={panes}
        menu={menu}
      >
        {children}
      </SemanticTab>
    </div>
  )
}

Tab.propTypes = {
  elmId: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.string,
  role: PropTypes.oneOf(['primary', 'secondary', undefined]),
  collapsing: PropTypes.bool,
  menu: PropTypes.object,
  panes: PropTypes.array,
  onTabChange: PropTypes.func,
  activeIndex: PropTypes.number,
  customCollapseRenderer: PropTypes.func,
  transparentMode: PropTypes.bool,
}

Tab.Pane = SemanticTab.Pane

export default Tab
